import { useQuery } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";

// ── Types ──────────────────────────────────────────────────────────────────────

/** A single source that grants a viewer access to the note. */
export interface AccessSource {
	type: "author" | "document_acl" | "ptp_contact" | "ptp_role" | "ptp_all_staff";
	label?: string;
	role?: string;
	role_label?: string;
}

/** Granular permission flags for a viewer. */
export interface ViewerPermissions {
	can_read: boolean;
	can_write: boolean;
	can_delete: boolean;
}

/** A single viewer who can see the note. */
export interface Viewer {
	_id: string;
	name: string;
	contact_id: string;
	/** ContactPersonal._id — used for profile image lookups. */
	contact_personal_id: string;
	access_sources: AccessSource[];
	permissions: ViewerPermissions;
}

/** Response envelope from `GET /note/user/{id}/visibility`. */
export interface NoteVisibilityResponse {
	viewers: Viewer[];
	viewer_count: number;
	includes_all_staff: boolean;
	visibility_mode: "normal" | "cp_only";
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export interface UseNoteVisibilityOptions {
	/** The parent note ID. When `null` the query is disabled. */
	noteId: string | null;
	/** Whether the confidential toggle is active. */
	confidential: boolean;
	/** Live category ID from the note form. When provided, the server uses this to compute permissions instead of the saved category. */
	category?: string | null;
	/** Explicitly disable the query (e.g. during initial load). */
	enabled?: boolean;
}

/**
 * Fetch the list of people who can see a given note.
 *
 * Ported from my-pymble-portal; uses this app's usePymbleApi (retry simplified
 * to a fixed count — the portal's authAwareRetry isn't exported here).
 */
export const useNoteVisibility = (options: UseNoteVisibilityOptions) => {
	const { callApi, isAuthenticated } = usePymbleApi();

	return useQuery<NoteVisibilityResponse>({
		queryKey: ["note-visibility", options.noteId, options.confidential, options.category ?? null],
		queryFn: async () => {
			let endpoint = `note/user/${encodeURIComponent(options.noteId!)}/visibility?confidential=${options.confidential}`;
			if (options.category) {
				endpoint += `&category=${encodeURIComponent(options.category)}`;
			}
			return callApi("GET", endpoint);
		},
		enabled: isAuthenticated && !!options.noteId && options.enabled !== false,
		staleTime: 30_000,
		retry: 1,
		refetchOnWindowFocus: false,
	});
};
